<template>
  <section
    class="invoice-preview-wrapper"
    style="position: relative"
    role="main"
    aria-label="Détail du bon de retour inter-agence"
    tabindex="-1"
  >
    <!-- Alerte : Aucun élément trouvé -->
    <b-alert
      variant="danger"
      :show="returnVoucher === undefined"
      aria-live="assertive"
      role="alert"
    >
      <h4 class="alert-heading">
        Erreur lors de la récupération du bon de retour
      </h4>
      <div class="alert-body">
        Aucun bon de retour trouvé avec cet identifiant. Consultez la
        <b-link
          class="alert-link"
          :to="{ name: 'bon-retour-inter-agence-list' }"
        >
          liste des bons de retour
        </b-link>
        pour d'autres retours.
      </div>
    </b-alert>

    <b-row
      v-if="returnVoucher"
      class="invoice-preview"
      aria-label="Contenu du bon de retour inter-agence"
    >
      <!-- Col: Left (Invoice Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-card
          v-if="returnVoucher"
          no-body
          class="invoice-preview-card"
          aria-label="Carte de détail du bon de retour inter-agence"
        >
          <!-- Header -->
          <div
            class="d-flex justify-content-center flex-md-row flex-column invoice-spacing mt-0"
            aria-label="En-tête du bon de retour inter-agence"
          >
            <div style="color: black">
              <div class="logo-wrapper">
                <brand-logo
                  class="inter-agency-print-logo"
                  width="300px"
                  height="100%"
                />
                <h3 class="text-primary invoice-logo" />
              </div>
            </div>
          </div>

          <b-card-body class="d-flex flex-row justify-content-between">
            <div style="color: black">
              <div class="ml-1">
                <h1 style="color: black; font-size: 30px">
                  Bon de retour inter-agence
                </h1>
              </div>
              <div>
                <h3
                  class="ml-1"
                  style="color: black"
                >
                  Date de création :
                  <span style="color: black"> {{ formatFn(returnVoucher.created_at) }}</span>
                </h3>
              </div>
            </div>
            <div
              class="d-flex justify-content-between flex-md-row flex-column invoice-spacing mt-0 mr-1 mb-2 ml-2"
              aria-label="Informations société de livraison"
              style="border: 2px solid black"
            >
              <b-card-text
                class="ml-2 mr-2 mt-2 mb-1 h3"
                style="color: black"
              >
                <strong>{{ company.name }} :</strong> {{ company.tagline || 'Société de livraison à domicile' }}
              </b-card-text>
              <b-card-text
                class="ml-2 h3 mr-2 mb-1"
                style="color: black"
              >
                <strong>Matricule fiscale :</strong> {{ company.tax_identification_number }}
              </b-card-text>
              <b-card-text
                class="ml-2 mr-2 mb-1 h3"
                style="color: black"
              >
                <strong>Adresse : </strong>{{ company.address }}
              </b-card-text>
              <b-card-text
                class="ml-2 mr-2 mb-1 h3"
                style="color:black"
              >
                <strong>Tel :</strong> {{ company.phone1 }}
              </b-card-text>
            </div>

          </b-card-body>
          <!-- Invoice Description: Table -->
          <div
            v-if="isLoading === true"
            class="text-center mb-2"
          >
            <b-spinner
              variant="primary"
              label="Text Centered"
            />
          </div>
          <b-card-body class="invoice-padding pt-0">
            <b-table-lite
              class="ta"
              responsive
              :items="orders"
              :fields="[
                { key: 'bl', label: 'Référence BL' },
                { key: 'gouvernorat', label: 'Gouvernorat' },
                { key: 'tel', label: 'Téléphone' },
                { key: 'destinataire', label: 'Destinataire' },
                { key: 'adresse', label: 'Adresse' },
                { key: 'prix', label: 'Prix TTC (TND)' },
                { key: 'prix_de_retour', label: 'Frais de retour (TND)' },
              ]"
              aria-label="Tableau des colis du bon de retour inter-agence"
            />
          </b-card-body>
          <!-- Invoice Description: Total -->

          <b-card-body class="d-flex justify-content-between mt-5">
            <!-- Col: Total -->
            <div class="mb-5 ml-1">
              <h3 style="color: black; font-size: 25px">
                Signature du responsable
              </h3>
            </div>
            <div>
              <h1 style="color: black; font-size: 25px">
                Total TTC : {{ returnVoucher.total_price.toFixed(3) }} TND
              </h1>
              <h1 style="color: black; font-size: 25px">
                Total frais de retour : {{ returnVoucher.total_return_price.toFixed(3) }} TND
              </h1>
            </div>
          </b-card-body>

        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions"
        aria-label="Actions du bon de retour inter-agence"
      >
        <b-card>
          <!-- Bouton : Imprimer -->
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            :class="{ 'disabled-cursor': isLoading }"
            :disabled="isLoading"
            aria-label="Imprimer le bon de retour inter-agence"
            @click="printInvoice"
          >
            Imprimer
          </b-button>
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import { ref } from '@vue/composition-api'
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BTableLite,
  BCardText,
  BButton,
  BAlert,
  BLink,
  VBToggle,
} from 'bootstrap-vue'

import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTableLite,
    BCardText,
    BButton,
    BAlert,
    BLink,
  },
  data() {
    return {
      returnVoucher: [],
      orders: [],
      company: {},
      isLoading: false,
    }
  },
  created() {
    this.company = storeAuth.state.company
    this.id = this.$route.params.id
    this.getReturnVoucher()
  },
  methods: {
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    async getReturnVoucher() {
      try {
        this.isLoading = true
        const response = await axios.get(`/api/return-vouchers/inter-agency-return/detail/${this.$route.params.id}`)
        this.returnVoucher = response.data
        const { orders } = this.returnVoucher
        for (let i = 0; i < orders.length; i += 1) {
          const data = {
            bl: orders[i].id,
            gouvernorat: orders[i].governorate,
            tel: orders[i].phone1,
            destinataire: orders[i].name,
            adresse: orders[i].address,
            prix: orders[i].price_ttc,
            prix_de_retour: orders[i].price_return,
          }
          this.orders.push(data)
          this.isLoading = false
        }
      } catch (error) {
        // Handle error
      }
    },
  },
  setup() {
    const invoiceData = ref(null)
    const paymentDetails = ref({})
    const sommeFrais = 0
    const total = 0
    const tax = 0
    const sommeHT = 0
    const printInvoice = () => {
      window.print()
    }

    return {
      invoiceData,
      paymentDetails,
      printInvoice,
      sommeHT,
      tax,
      total,
      sommeFrais,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";
table,
td,
th {
  padding: 10px;
  text-align: left;
  border: 1px solid black;
}
footer {
  bottom: 0;
  height: auto; /* Footer height */
}
table {
  width: 100%;
  border-collapse: collapse;
}
.mycolor {
  background-color: #EA2F2F;
}
.contact {
  background-color: #EA2F2F;
  padding: 10px;
  border-radius: 20px;
}
</style>

<style lang="scss">
@media print {
  html, body {
    height: 99%;
  }

  .inter-agency-print-logo {
    display: none !important;
  }
  .ta {
    overflow-x: visible;
    font-size: 20px;
    color: black;
  }
  footer {
    position: fixed;
    bottom: 0;
    width: 100%;
    height: auto; /* Footer height */
  }

  .table thead th {
    border: 2px solid black;
  }

  body {
    background-color: transparent !important;
  }
  nav.header-navbar {
    display: none;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content.app-content {
    margin-left: 0;
    padding-top: 2rem !important;
  }
  .mycolor {
    background-color: #EA2F2F;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .contact {
    background-color: #EA2F2F;
    padding: 20px;
    border-radius: 25px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  footer footer-light footer-static {
    display: inline-block;
  }
  footer.footer {
    display: none;
  }

  .card {
    background-color: transparent;
    box-shadow: none;
  }
  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        .card-body:nth-of-type(2) {
          .row {
            > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
  table,
  td,
  th {
    padding: 10px;
    text-align: left;
    border: 1px solid black;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }
}
</style>
